import {Dispatch, FC, SetStateAction, useEffect, useState, useCallback} from 'react'
import {Modal} from 'react-bootstrap'
import {useForm, FormProvider} from 'react-hook-form'
import {yupResolver} from '@hookform/resolvers/yup'
import * as Yup from 'yup'
import {KTSVG} from '../../../../../../../../_metronic/helpers'
import {InputTextWrapper} from '../../../../../../components/UI/Form/Input'
import {SelectWrapper, SelectAsyncWrapper} from '../../../../../../components/UI/Form/Input'
import {getSnackBarToast} from '../../../../../../util/snackBar'
import {GuestModel} from '../../../guests/model'
import {useCentralServices} from '../../../../hooks/useCentralServices'
import {CreateGuestFaceToFaceModel} from '../../model/createGuestFaceToFace.model'
import {newFromRelay} from '../../../../../central/services'
import useEnterpriseService, {
  ENTERPRISE_SEARCH_MIN_LENGTH,
} from '../../../../../central/hooks/useEnterpriseService'
import {getDataHuespedPopup, getListCargosService} from '../../../../services/booking.service'

interface Props {
  show: boolean
  setShow: Dispatch<SetStateAction<boolean>>
  campamentoId: string
  onGuestCreated: (guest: GuestModel) => void
  numeroDocumento?: string
  tipoDocumentoId?: string
  showCargoField?: boolean
}

const createGuestSchema = Yup.object().shape({
  TipoDoc: Yup.string().required('Tipo de documento es requerido'),
  NumeroDocumento: Yup.string().required('Número de documento es requerido'),
  Nombres: Yup.string().required('Nombres es requerido'),
  ApellidoPaterno: Yup.string().required('Apellido Paterno es requerido'),
  ApellidoMaterno: Yup.string().required('Apellido Materno es requerido'),
  Sexo: Yup.string().required('Sexo es requerido'),
  EmpresaSubcontratista: Yup.string().required('Empresa Subcontratista es requerida'),
  Email: Yup.string().email('Email inválido').required('Email es requerido'),
  telefono: Yup.string()
    .required('Teléfono es requerido')
    .matches(/^[0-9+\-\s]*$/, 'Teléfono inválido'),
})

const ModalCreateGuest: FC<Props> = ({
  show,
  setShow,
  campamentoId,
  onGuestCreated,
  numeroDocumento = '',
  tipoDocumentoId = '01',
  showCargoField = false,
}) => {
  const {getMasterTable} = useCentralServices()
  const [sexOptions, setSexOptions] = useState<any[]>([])
  const [cargoOptions, setCargoOptions] = useState<any[]>([{value: '', label: 'CUALQUIERA'}])
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const {getEnterpriseSearchOptions, getEnterpriseOptionById} = useEnterpriseService()
  const [empresaSubcontratistaOption, setEmpresaSubcontratistaOption] = useState<{
    value: any
    label: string
  } | null>(null)
  const [disabledData, setDisabledData] = useState(false)

  const methods = useForm<CreateGuestFaceToFaceModel>({
    resolver: yupResolver(createGuestSchema),
    defaultValues: {
      TipoDoc: tipoDocumentoId,
      NumeroDocumento: numeroDocumento,
      CargoId: '',
    },
  })

  const {
    control,
    setValue,
    handleSubmit,
    setError,
    reset,
    formState: {errors},
  } = methods

  const tipoDocumentoOptions = [
    {value: '00', label: 'FOTOCHECK'},
    {value: '01', label: 'DNI'},
    {value: '04', label: 'CE'},
    {value: '07', label: 'PASAPORTE'},
  ]

  const initValues = async () => {
    const defaultEmail = `${numeroDocumento}@gmail.com`
    const defaultTelefono = '999999999'
    try {
      const {data} = await getDataHuespedPopup(numeroDocumento)
      if (data.data) {
        const guestData = data.data
        setValue('Nombres', guestData?.Nombres || '')
        setValue('ApellidoPaterno', guestData?.ApePat || '')
        setValue('ApellidoMaterno', guestData?.ApeMat || '')
        setValue('Sexo', guestData?.Sexo || '')
        setValue('EmpresaSubcontratista', guestData?.EmpSubContratistaId || '')
        setValue('Email', guestData?.email || defaultEmail)
        setValue('telefono', guestData?.telefono || defaultTelefono)
        const subcontratistaOption = await getEnterpriseOptionById(guestData?.EmpSubContratistaId)
        setEmpresaSubcontratistaOption(subcontratistaOption)
        setDisabledData(true)
      } else {
        setValue('Email', defaultEmail)
        setValue('telefono', defaultTelefono)
        setEmpresaSubcontratistaOption(null)
        setDisabledData(false)
      }
    } catch (error) {
      setValue('Email', defaultEmail)
      setValue('telefono', defaultTelefono)
      setDisabledData(false)
      console.log(error)
    }
  }

  useEffect(() => {
    getSexList()
    initValues()
    if (showCargoField) {
      getCargosFn()
    }
  }, [])

  // useEffect(() => {
  //   if (show && numeroDocumento) {
  //     reset({
  //       TipoDoc: tipoDocumentoId,
  //       NumeroDocumento: numeroDocumento,
  //     })
  //   }
  // }, [show, numeroDocumento, tipoDocumentoId, reset])

  const getSexList = async () => {
    try {
      // Usar false para obtener por código en lugar de ID
      const data = await getMasterTable('Sexo', false)
      const filterData = data.filter((item: any) => item.codigo !== '3')
      const mappedSex = filterData.map((item: any) => ({
        value: item.codigo,
        label: item.nombreCorto || item.nombre,
      }))
      setSexOptions(mappedSex)
    } catch (error) {
      console.error('Error getting sex list:', error)
      setSexOptions([])
    }
  }

  const getCargosFn = async () => {
    try {
      const response = await getListCargosService()
      if (!response.data.error && response.data.data) {
        const mappedData = response.data.data.map((item: any) => ({
          value: item.Id,
          label: item.NombreCorto || item.NombreLargo,
        }))
        const allData = [{value: '', label: 'CUALQUIERA'}, ...mappedData]
        setCargoOptions(allData)
      }
    } catch (error) {
      setCargoOptions([{value: '', label: 'CUALQUIERA'}])
    }
  }

  const handleGetEmpresaSubcontratista = (val: any) => {
    const value = val ? val : ''
    setValue('EmpresaSubcontratista', value.value)
    setError('EmpresaSubcontratista', {})
    setEmpresaSubcontratistaOption(val ?? null)
  }

  const loadOptionsEmpresa = useCallback(async (inputValue: string) => {
    if (!inputValue || inputValue.trim().length < ENTERPRISE_SEARCH_MIN_LENGTH) {
      return []
    }
    return getEnterpriseSearchOptions(inputValue.trim())
  }, [])

  const handleClose = () => {
    setShow(false)
    reset()
    setEmpresaSubcontratistaOption(null)
  }

  const onSubmit = async (data: CreateGuestFaceToFaceModel) => {
    console.log('data', data)
    try {
      setIsSubmitting(true)
      // Los datos ya están en el formato correcto según el modelo
      const guestData: CreateGuestFaceToFaceModel = {
        TipoDoc: data.TipoDoc,
        NumeroDocumento: data.NumeroDocumento,
        Nombres: data.Nombres,
        ApellidoPaterno: data.ApellidoPaterno,
        ApellidoMaterno: data.ApellidoMaterno,
        Sexo: data.Sexo,
        CargoId: data.CargoId || '',
        EmpresaSubcontratista: data.EmpresaSubcontratista || '',
        Email: data.Email || '',
        telefono: data.telefono || '',
      }
      // Agregar CampamentoId para el servicio
      const response = await newFromRelay({...guestData, CampamentoId: campamentoId})
      if (!response.data.error) {
        getSnackBarToast('Huésped creado con éxito', 'success')
        onGuestCreated(response.data.data)
        handleClose()
      } else {
        getSnackBarToast(response.data.message, 'error')
      }
    } catch (error: any) {
      getSnackBarToast(error?.response?.data?.message || 'Error al crear huésped', 'error')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Modal
      className='modal fade'
      show={show}
      dialogClassName='modal-lg'
      aria-hidden='true'
      onHide={handleClose}
    >
      <div className='modal-content'>
        <div className='modal-header'>
          <h5 className='modal-title'>Crear Huésped</h5>
          <div className='btn btn-icon btn-sm btn-active-light-primary ms-2' onClick={handleClose}>
            <KTSVG path='/media/icons/duotune/arrows/arr061.svg' className='svg-icon-2x' />
          </div>
        </div>
        <div className='modal-body'>
          <FormProvider {...methods}>
            <form>
              <div className='row p-3'>
                <div className='col-sm-12 col-lg-6 mb-3'>
                  <SelectWrapper
                    labelText='Tipo de Documento'
                    name='TipoDoc'
                    data={tipoDocumentoOptions}
                    required
                    disabled={disabledData}
                  />
                </div>
                <div className='col-sm-12 col-lg-6 mb-3'>
                  <InputTextWrapper
                    labelText='Número de Documento'
                    name='NumeroDocumento'
                    textPlaceholder='Ingrese el número de documento'
                    required
                    type='numeric'
                    disabled={disabledData}
                  />
                </div>
                <div className='col-sm-12 col-lg-4 mb-3'>
                  <InputTextWrapper
                    labelText='Nombres'
                    name='Nombres'
                    textPlaceholder='Ingrese los nombres'
                    required
                    disabled={disabledData}
                  />
                </div>
                <div className='col-sm-12 col-lg-4 mb-3'>
                  <InputTextWrapper
                    labelText='Apellido Paterno'
                    name='ApellidoPaterno'
                    textPlaceholder='Ingrese el apellido paterno'
                    required
                    disabled={disabledData}
                  />
                </div>
                <div className='col-sm-12 col-lg-4 mb-3'>
                  <InputTextWrapper
                    labelText='Apellido Materno'
                    name='ApellidoMaterno'
                    textPlaceholder='Ingrese el apellido materno'
                    required
                    disabled={disabledData}
                  />
                </div>
                <div className='col-sm-12 col-lg-6 mb-3'>
                  <SelectWrapper
                    labelText='Sexo'
                    name='Sexo'
                    data={sexOptions}
                    required
                    disabled={disabledData}
                  />
                </div>
                {showCargoField && (
                  <div className='col-sm-12 col-lg-6 mb-3'>
                    <SelectWrapper
                      labelText='Cargo'
                      name='CargoId'
                      data={cargoOptions}
                    />
                  </div>
                )}
                <div className='col-sm-12 col-lg-6 mb-3'>
                  <SelectAsyncWrapper
                    labelText='Empresa Subcontratista'
                    name='EmpresaSubcontratista'
                    loadOptions={loadOptionsEmpresa}
                    onChangeValue={handleGetEmpresaSubcontratista}
                    textPlaceholder='Escribe al menos 4 letras...'
                    value={empresaSubcontratistaOption}
                    required
                  />
                </div>
                <div className='col-sm-12 col-lg-6 mb-3'>
                  <InputTextWrapper
                    labelText='Email'
                    name='Email'
                    textPlaceholder='Ingrese el email'
                    type='any'
                    required
                  />
                </div>
                <div className='col-sm-12 col-lg-6 mb-3'>
                  <InputTextWrapper
                    labelText='Teléfono'
                    name='telefono'
                    textPlaceholder='Ingrese el teléfono'
                    type='telephone'
                    required
                  />
                </div>
              </div>
              <div className='d-flex justify-content-end mb-3'>
                <button
                  type='button'
                  className='btn btn-light-primary me-3'
                  onClick={handleClose}
                  disabled={isSubmitting}
                >
                  Cancelar
                </button>
                <button type='button' className='btn btn-primary' onClick={handleSubmit(onSubmit)}>
                  {isSubmitting ? 'Creando...' : 'Crear Huésped'}
                </button>
              </div>
            </form>
          </FormProvider>
        </div>
      </div>
    </Modal>
  )
}

export {ModalCreateGuest}
